import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ArrowRight, Building, BarChart2, Award, Globe, Users, Calendar, ShoppingCart, Heart, Home } from "lucide-react";
import { Helmet } from "react-helmet";

const caseStudies = [
  {
    title: "How an e-commerce company increased conversion by 45% with competitive insights",
    industry: "E-commerce",
    results: "45% increase in conversion",
    duration: "3 months",
    description: "Learn how this e-commerce business utilized Benchr's competitive intelligence platform to optimize pricing strategies and increase their conversion rate by 45% within just 3 months.",
    position: "Marketing Executive",
    companyType: "E-commerce Industry",
    icon: <ShoppingCart className="w-3 h-3 mr-1" />
  },
  {
    title: "Global beauty retailer optimizes inventory with market data",
    industry: "Beauty & Cosmetics",
    results: "28% reduction in stockouts",
    duration: "6 months",
    description: "This beauty retailer leveraged Benchr's market data to predict trends and optimize their inventory management, resulting in a 28% reduction in stockouts and improved customer satisfaction.",
    position: "Supply Chain Director",
    companyType: "Beauty & Cosmetics Industry",
    icon: <Heart className="w-3 h-3 mr-1" />
  },
  {
    title: "Furniture retailer gains market share with competitive pricing",
    industry: "Home & Furniture",
    results: "18% market share increase",
    duration: "12 months",
    description: "This furniture company implemented Benchr's competitive pricing intelligence to identify optimal price points across their catalog, resulting in an 18% increase in market share within a year.",
    position: "Pricing Manager",
    companyType: "Home & Furniture Industry",
    icon: <Home className="w-3 h-3 mr-1" />
  }
];

const CaseStudies = () => {
  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>Success Stories | benchr.ai - E-commerce Competitive Intelligence Platform</title>
        <meta name="description" content="Explore real-world success stories of how businesses leverage Benchr's AI-powered competitive intelligence platform to optimize pricing, increase market share, and drive e-commerce growth." />
        <meta name="keywords" content="e-commerce case studies, competitive intelligence success, pricing optimization, market share growth, retail analytics ROI, e-commerce optimization results" />
        <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Benchr Success Stories - E-commerce Competitive Intelligence Results",
            "description": "Real-world case studies showing how businesses leverage AI-powered competitive intelligence to drive e-commerce growth and market share.",
            "keywords": ["e-commerce case studies", "competitive intelligence success", "pricing optimization", "market share growth"],
            "publisher": {
              "@type": "Organization",
              "name": "Benchr",
              "logo": {
                "@type": "ImageObject",
                "url": "/lovable-uploads/5b3fc61d-09cc-4356-aca7-ef3a45266769.png"
              }
            }
          }
        `}
        </script>
      </Helmet>
      <Navbar />
      <main className="flex-grow pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-benchr-50 dark:bg-benchr-900">
          <div className="container max-w-7xl mx-auto px-6 md:px-10">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-benchr-900 dark:text-white mb-6 tracking-tight">
                Client Success Stories
              </h1>
              <p className="text-xl text-benchr-600 dark:text-benchr-300 mb-8">
                Discover how businesses across various industries have leveraged Benchr to gain competitive insights and drive growth.
              </p>
            </div>
          </div>
        </section>

        {/* Case Studies Grid */}
        <section className="py-16 bg-white dark:bg-benchr-950">
          <div className="container max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {caseStudies.map((study, index) => (
                <div 
                  key={index}
                  className="bg-white dark:bg-benchr-900 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 border border-benchr-100 dark:border-benchr-800 p-6"
                >
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-benchr-100 dark:bg-benchr-800 text-benchr-800 dark:text-benchr-200">
                      {study.icon}
                      {study.industry}
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                      <BarChart2 className="w-3 h-3 mr-1" />
                      {study.results}
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                      <Calendar className="w-3 h-3 mr-1" />
                      {study.duration}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-benchr-900 dark:text-white mb-3">
                    {study.title}
                  </h3>
                  <p className="text-benchr-600 dark:text-benchr-300 mb-4">
                    {study.description}
                  </p>
                  <div className="mt-auto pt-3 border-t border-benchr-100 dark:border-benchr-800">
                    <p className="font-semibold text-benchr-900 dark:text-white">
                      {study.position}
                    </p>
                    <p className="text-sm text-benchr-500 dark:text-benchr-400">
                      {study.companyType}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="py-16 bg-benchr-50 dark:bg-benchr-900/60">
          <div className="container max-w-7xl mx-auto px-6 md:px-10">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-benchr-900 dark:text-white mb-6 tracking-tight">
                What Our Clients Say
              </h2>
            </div>
            
            <div className="max-w-4xl mx-auto bg-white dark:bg-benchr-800 rounded-2xl p-8 shadow-lg">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0">
                  <img 
                    src="https://images.unsplash.com/photo-1487058792275-0ad4aaf24ca7" 
                    alt="Client" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <blockquote className="text-lg text-benchr-600 dark:text-benchr-200 italic mb-4">
                    "Benchr has completely transformed how we approach our competitive strategy. The depth of the data and insights we've gained has been invaluable for our business growth. I can't imagine running our e-commerce operations without it now."
                  </blockquote>
                  <div className="flex items-center">
                    <div>
                      <p className="font-semibold text-benchr-900 dark:text-white">Marketing Executive</p>
                      <p className="text-sm text-benchr-500 dark:text-benchr-400">Beauty & Cosmetics Industry</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* CTA Section */}
        <section className="py-16 bg-white dark:bg-benchr-950">
          <div className="container max-w-7xl mx-auto px-6 md:px-10">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-benchr-900 dark:text-white mb-4 tracking-tight">
                Ready to achieve similar results?
              </h2>
              <p className="text-lg text-benchr-600 dark:text-benchr-300 mb-8">
                Learn how Benchr can help you gain competitive intelligence and drive growth for your business.
              </p>
              <a 
                href="/signup"
                className="inline-flex items-center justify-center px-6 py-3.5 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-lg shadow-sm hover:shadow-md border border-gray-300 transition-all duration-300 ease-in-out"
              >
                <div className="mr-2 flex items-center justify-center">
                  <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                </div>
                Sign up with Google
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CaseStudies;
